"use client";
// Portal de proveedores (restaurantes y caballerizas): entrada, disponibilidad, solicitudes y llegadas.
// Con servidor: /api/proveedor/* con el token de la sesión. En la vista previa: el almacén del navegador, con las mismas reglas.
import type { EstadoSolicitud } from "@/lib/reservas-proveedor-reglas";
import { almacenNavegador, type Almacen } from "./proveedor";
import * as L from "./reservas-local";

export const LLAVE_SESION_PROVEEDOR = "proveedor-sesion-v1";
export type SesionProveedor = { token: string; id: string; nombre: string; tipo: string; lugarId?: string };
export type LugarProveedor = { id: string; nombre: string; tipo: string; ciudad: string | null; principal: boolean; operable: boolean; detalle?: string };

/** Sesión del portal guardada en este navegador (solo para no pedir la contraseña en cada pantalla). */
export function leerSesionProveedor(): SesionProveedor | null {
  try {
    const s = JSON.parse(localStorage.getItem(LLAVE_SESION_PROVEEDOR) ?? "null");
    return s && typeof s.token === "string" && typeof s.id === "string" && typeof s.nombre === "string" && typeof s.tipo === "string" ? s : null;
  } catch { return null; }
}
export function guardarSesionProveedor(s: SesionProveedor | null) {
  try { if (s) localStorage.setItem(LLAVE_SESION_PROVEEDOR, JSON.stringify(s)); else localStorage.removeItem(LLAVE_SESION_PROVEEDOR); } catch { /* sin almacenamiento */ }
}
export function seleccionarLugarProveedor(lugarId: string | undefined) {
  const sesion = leerSesionProveedor();
  if (sesion) guardarSesionProveedor({ ...sesion, lugarId });
}
export const tokenProveedor = () => leerSesionProveedor()?.token ?? null;

export type HoraReserva = { hora: string; lugares: number; usados: number };
export type DiaReserva = { fecha: string; horas: HoraReserva[] };
export type DisponibilidadProveedor = { tipo: string; cupoMaximo: number; desde: string; dias: DiaReserva[] };
export type SolicitudProveedor = {
  id: string; folio: string; fecha: string; hora: string; personas: number; estado: EstadoSolicitud | string; clienteNombre: string; clienteTelefono: string; clienteCorreo: string | null;
  montoCentavos: number; metodoPago: string; cobrado: boolean; codigoQr: string | null; motivo: string | null; llegadaEn: string | null; respondidaEn: string | null; creadoEn: string;
};
export type FiltroSolicitudes = { estado?: string; desde?: string; hasta?: string; fecha?: string };
export type CambioLugares = { fecha: string; hora: string; lugares: number };
export type AccionReserva = "aceptar" | "rechazar" | "cancelar" | "llego" | "noLlego";

export type FotoProveedor = { id: string; archivo: string; alt: string; orden: number };
export type PerfilProveedor = { nombre: string; contacto: string; telefono: string; correo: string | null; ciudad: string | null; tipo: string; cupoMaximo: number };
export type CambiosPerfilProveedor = Pick<PerfilProveedor, "nombre" | "contacto" | "telefono" | "correo" | "ciudad">;

export type PortalProveedor = {
  /** Usuario y contraseña del proveedor. Lanza Error con mensaje claro (incluido el bloqueo por intentos). */
  entrar(usuario: string, clave: string): Promise<SesionProveedor>;
  lugares(): Promise<LugarProveedor[]>;
  disponibilidad(desde: string): Promise<DisponibilidadProveedor>;
  guardarDisponibilidad(cambios: CambioLugares[]): Promise<{ ok: true; guardados: number }>;
  solicitudes(f: FiltroSolicitudes): Promise<SolicitudProveedor[]>;
  responder(id: string, accion: AccionReserva, motivo?: string): Promise<{ mensaje: string; estado: string; codigoQr: string | null }>;
  /** Por lluvia: mueve los paseos aceptados de un día a otro. Los que no caben se devuelven en sinLugar. */
  reprogramarPorLluvia(fechaOrigen: string, fechaNueva: string): Promise<{ movidas: number; sinLugar: string[]; total: number }>;
  /** Fotos del lugar: la primera es la que ven los clientes. */
  fotos(): Promise<FotoProveedor[]>;
  subirFoto(archivo: string, alt: string): Promise<FotoProveedor>;
  quitarFoto(id: string): Promise<{ ok: true }>;
  hacerPrincipal(id: string): Promise<{ ok: true }>;
  /** Datos visibles del negocio. Los horarios y fotos se administran en sus propias secciones. */
  perfil(): Promise<PerfilProveedor>;
  guardarPerfil(datos: CambiosPerfilProveedor): Promise<PerfilProveedor>;
};

function servidorPortal(): PortalProveedor {
  const pedir = async (url: string, init?: RequestInit) => {
    const t = tokenProveedor();
    const sesion = leerSesionProveedor();
    const conLugar = sesion?.lugarId && !url.includes("/login") && !url.includes("/lugares")
      ? `${url}${url.includes("?") ? "&" : "?"}lugarId=${encodeURIComponent(sesion.lugarId)}` : url;
    const r = await fetch(conLugar, { ...init, headers: { "content-type": "application/json", ...(t ? { authorization: `Bearer ${t}` } : {}) } });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      if (r.status === 401 && sesion) guardarSesionProveedor(null);
      throw new Error(d.mensaje ?? "Error");
    }
    return d;
  };
  const q = (o: Record<string, string | undefined>) => new URLSearchParams(Object.entries(o).filter(([, v]) => v) as [string, string][]).toString();
  return {
    async entrar(usuario, clave) {
      const d = await pedir("/api/proveedor/login", { method: "POST", body: JSON.stringify({ usuario, clave }) });
      const s: SesionProveedor = { token: "", id: d.id, nombre: d.nombre, tipo: d.tipo }; // el token va en la cookie protegida
      guardarSesionProveedor(s);
      return s;
    },
    lugares: () => pedir("/api/proveedor/lugares"),
    disponibilidad: (desde) => pedir(`/api/proveedor/disponibilidad?${q({ desde })}`),
    guardarDisponibilidad: (cambios) => pedir("/api/proveedor/disponibilidad", { method: "PUT", body: JSON.stringify({ cambios }) }),
    solicitudes: (f) => pedir(`/api/proveedor/solicitudes?${q({ estado: f.estado, desde: f.desde, hasta: f.hasta, fecha: f.fecha })}`),
    responder: (id, accion, motivo) => pedir(`/api/proveedor/solicitudes/${encodeURIComponent(id)}/${accion}`, { method: "POST", body: JSON.stringify({ motivo }) }),
    reprogramarPorLluvia: (fechaOrigen, fechaNueva) => pedir("/api/proveedor/lluvia", { method: "POST", body: JSON.stringify({ fechaOrigen, fechaNueva }) }),
    fotos: () => pedir("/api/proveedor/fotos"),
    subirFoto: (archivo, alt) => pedir("/api/proveedor/fotos", { method: "POST", body: JSON.stringify({ archivo, alt }) }),
    quitarFoto: (id) => pedir(`/api/proveedor/fotos/${encodeURIComponent(id)}`, { method: "DELETE" }),
    hacerPrincipal: (id) => pedir(`/api/proveedor/fotos/${encodeURIComponent(id)}`, { method: "PUT" }),
    perfil: () => pedir("/api/proveedor/perfil"),
    guardarPerfil: (datos) => pedir("/api/proveedor/perfil", { method: "PUT", body: JSON.stringify(datos) }),
  };
}

/** En la vista previa, el token es el id del proveedor (no hay servidor que lo firme). */
function localPortal(a: Almacen): PortalProveedor {
  const cuenta = () => {
    const s = leerSesionProveedor();
    if (!s) throw new Error("Tu sesión venció. Vuelve a entrar.");
    return s;
  };
  const yo = () => cuenta().lugarId ?? cuenta().id;
  return {
    async entrar(usuario, clave) {
      const p = await L.entrarProveedorLocal(a, usuario, clave);
      const s: SesionProveedor = { token: p.id, ...p };
      guardarSesionProveedor(s);
      return s;
    },
    lugares: async () => L.lugaresDeProveedorLocal(a, cuenta().id),
    disponibilidad: async (desde) => L.disponibilidadDeProveedorLocal(a, yo(), desde),
    guardarDisponibilidad: async (cambios) => L.guardarDisponibilidadLocal(a, yo(), cambios),
    solicitudes: async (f) => L.solicitudesDeProveedorLocal(a, yo(), f) as Promise<SolicitudProveedor[]>,
    responder: async (id, accion, motivo) => L.responderSolicitudLocal(a, id, accion, motivo, yo()),
    reprogramarPorLluvia: async (fechaOrigen, fechaNueva) => L.reprogramarPorLluviaLocal(a, yo(), fechaOrigen, fechaNueva),
    fotos: async () => L.fotosDeProveedorLocal(a, yo()),
    subirFoto: async (archivo, alt) => L.subirFotoProveedorLocal(a, yo(), { archivo, alt }),
    quitarFoto: async (id) => L.quitarFotoProveedorLocal(a, yo(), id),
    hacerPrincipal: async (id) => L.hacerPrincipalProveedorLocal(a, yo(), id),
    perfil: async () => L.perfilProveedorLocal(a, yo()),
    guardarPerfil: async (datos) => L.guardarPerfilProveedorLocal(a, yo(), datos),
  };
}

export const portalProveedor = (): Promise<PortalProveedor> =>
  process.env.NEXT_PUBLIC_EXPORT === "1" ? almacenNavegador("demo/panel-datos.json").then(localPortal) : Promise.resolve(servidorPortal());
